/**
 * Who holds the key.
 *
 * nicemail.ca needs two operations: sign an event (relay auth, publishing the
 * contact list) and NIP-44 decrypt (opening every gift wrap). Both can happen
 * locally, with the key in this tab, or remotely, with the key on a phone
 * running a NIP-46 signer such as Amber.
 *
 * The remote path costs a relay round-trip per decrypt, and a gift wrap needs
 * *two* — the wrap, then the seal inside it. That is fine because plaintext is
 * cached in IndexedDB, so it is a one-time cost per message rather than per
 * view, but it does make the first sync of a large mailbox slow. Callers should
 * show progress, which is why `remote` is exposed.
 */
import { nip44, finalizeEvent, getPublicKey, type EventTemplate, type VerifiedEvent } from "nostr-tools";
import { BunkerSigner, parseBunkerInput, type BunkerPointer } from "nostr-tools/nip46";

export interface MailSigner {
  readonly pubkey: string;
  /** True when each operation is a network round-trip. */
  readonly remote: boolean;
  /** Human-readable, for the status line. */
  readonly kind: "local" | "amber";
  signEvent(draft: EventTemplate): Promise<VerifiedEvent>;
  nip44Decrypt(peerPubkey: string, ciphertext: string): Promise<string>;
  nip44Encrypt(peerPubkey: string, plaintext: string): Promise<string>;
  close(): void;
}

/** The key is in this tab. Fast, and gone on refresh. */
export class LocalSigner implements MailSigner {
  readonly pubkey: string;
  readonly remote = false;
  readonly kind = "local" as const;

  constructor(private readonly secret: Uint8Array) {
    this.pubkey = getPublicKey(secret);
  }

  async signEvent(draft: EventTemplate): Promise<VerifiedEvent> {
    return finalizeEvent(draft, this.secret);
  }

  async nip44Decrypt(peerPubkey: string, ciphertext: string): Promise<string> {
    return nip44.v2.decrypt(ciphertext, nip44.v2.utils.getConversationKey(this.secret, peerPubkey));
  }

  async nip44Encrypt(peerPubkey: string, plaintext: string): Promise<string> {
    return nip44.v2.encrypt(plaintext, nip44.v2.utils.getConversationKey(this.secret, peerPubkey));
  }

  close(): void {
    /* nothing to release */
  }
}

/** The key is on a phone. Every call is approved there. */
export class AmberSigner implements MailSigner {
  readonly remote = true;
  readonly kind = "amber" as const;

  private constructor(
    readonly pubkey: string,
    private readonly bunker: BunkerSigner,
  ) {}

  /** Where the signer is, so a reload can reconnect without another scan. */
  get pointer(): BunkerPointer {
    return this.bunker.bp;
  }

  /**
   * Connect to a signer.
   *
   * `input` is whatever the signer gave us: a `bunker://` URI pasted from
   * Amber, or an npub/NIP-05 it can resolve.
   */
  static async connect(
    input: string,
    clientSecret: Uint8Array,
    onAuthUrl?: (url: string) => void,
  ): Promise<AmberSigner> {
    const pointer = await parseBunkerInput(input.trim());
    if (!pointer) throw new Error("that is not a bunker:// connection string");
    const bunker = BunkerSigner.fromBunker(clientSecret, pointer, {
      onauth: onAuthUrl ?? (() => {}),
    });
    await bunker.connect();
    const pubkey = await bunker.getPublicKey();
    return new AmberSigner(pubkey, bunker);
  }

  /** Reconnect to a signer we have already been authorised by. */
  static async reconnect(
    pointer: BunkerPointer,
    clientSecret: Uint8Array,
    onAuthUrl?: (url: string) => void,
  ): Promise<AmberSigner> {
    const bunker = BunkerSigner.fromBunker(clientSecret, pointer, {
      onauth: onAuthUrl ?? (() => {}),
    });
    await bunker.connect();
    const pubkey = await bunker.getPublicKey();
    return new AmberSigner(pubkey, bunker);
  }

  /** Wait for a signer to come to us, from a `nostrconnect://` URI we published. */
  static async fromNostrConnect(
    uri: string,
    clientSecret: Uint8Array,
    abort: AbortSignal,
  ): Promise<AmberSigner> {
    const bunker = await BunkerSigner.fromURI(clientSecret, uri, {}, abort);
    const pubkey = await bunker.getPublicKey();
    return new AmberSigner(pubkey, bunker);
  }

  signEvent(draft: EventTemplate): Promise<VerifiedEvent> {
    return this.bunker.signEvent(draft);
  }

  nip44Decrypt(peerPubkey: string, ciphertext: string): Promise<string> {
    return this.bunker.nip44Decrypt(peerPubkey, ciphertext);
  }

  nip44Encrypt(peerPubkey: string, plaintext: string): Promise<string> {
    return this.bunker.nip44Encrypt(peerPubkey, plaintext);
  }

  close(): void {
    void this.bunker.close();
  }
}
